import { type FC, type ReactNode, useState } from 'react'

import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { OneDataCollection, RecordType } from '@factorialco/f0-react/dist/experimental'
import { Plus } from '@factorialco/f0-react/icons/app'
import { Delete, Pencil, Reset } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { EquipmentDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import app from 'modules/core/store/models/app'

import {
  safeAssetTranslateType,
  safeAssetTypeAsIcon,
} from 'modules/it_management/components/AssetsList/utils'
import {
  EditEquipmentModal,
  ItAssetModelFinder,
} from 'modules/job_catalog/components/ItAssetModelFinder'
import {
  EquipmentConfig,
  EquipmentData,
} from 'modules/job_catalog/components/ItAssetModelFinder/types'
import useAddJobCatalogNodeAttribute from 'modules/job_catalog/hooks/useAddJobCatalogNodeAttribute'
import useDeleteJobCatalogNodeAttribute from 'modules/job_catalog/hooks/useDeleteJobCatalogNodeAttribute'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { useSublevelCounts } from 'modules/job_catalog/hooks/useSublevelCounts'
import useUpdateJobCatalogEquipment from 'modules/job_catalog/hooks/useUpdateJobCatalogEquipment'
import {
  EquipmentRecord,
  isEquipmentRecord,
  mapEquipmentData,
} from 'modules/job_catalog/lib/equipmentUtils'
import {
  NODE_TYPES,
  NodeType,
  normalizeNodeType,
  shouldShowDeleteButton,
} from 'modules/job_catalog/lib/nodesUtils'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface EquipmentDataTableProps {
  nodeId: string
  nodeType: NodeType
  childType?: string
}

export const EquipmentDataTable: FC<EquipmentDataTableProps> = ({
  nodeId,
  nodeType: _nodeType,
  childType: _childType,
}) => {
  const [showEquipmentModal, setShowEquipmentModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingEquipment, setEditingEquipment] = useState<EquipmentRecord | null>(null)
  const [isOverrideMode, setIsOverrideMode] = useState(false)
  const [currentEquipment, setCurrentEquipment] = useState<EquipmentConfig[]>([])
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [totalEquipments, setTotalEquipments] = useState<number>(0)

  const { company } = useCurrentSession()
  const { addAttribute } = useAddJobCatalogNodeAttribute()
  const { deleteAttribute } = useDeleteJobCatalogNodeAttribute()
  const { updateEquipment } = useUpdateJobCatalogEquipment()
  const { openDialog } = useConfirmationDialog()
  const { counts: sublevelCounts } = useSublevelCounts(nodeId, _nodeType)
  const { canManageTree, canSeeItManagement } = useJobCatalogTreeVisibility()
  const shouldShowUpsell = !canSeeItManagement

  const source = useGraphqlDataCollectionSource(
    {
      document: EquipmentDataCollectionDocument,
      totalItemSummary: (totalItems: number) =>
        i18n.tp('job_catalog.nodes.it_management.total_item_summary', { count: totalItems }),
      mapData: (data) => {
        const attributes = data?.jobCatalog?.nodeAttributesConnection?.nodes ?? []
        const equipmentData = mapEquipmentData(attributes, nodeId, setCurrentEquipment)
        setTotalEquipments(data?.jobCatalog?.nodeAttributesConnection?.totalCount ?? 0)
        return equipmentData
      },
      mapPagination: (data) => ({
        total: data?.jobCatalog?.nodeAttributesConnection?.totalCount ?? 0,
      }),
      perPage: 10,
      primaryActions:
        totalEquipments && canManageTree
          ? () => ({
              label: i18n.t('job_catalog.equipment.actions.assign'),
              onClick: () => {
                track(trackingEvents.jobCatalog.assignDeviceStart, {
                  event_category: 'Organizations',
                  node_type: normalizeNodeType(nodeId),
                  node_id: nodeId,
                  from: 'it_management_tab',
                })
                setShowEquipmentModal(true)
              },
              icon: Plus,
            })
          : undefined,
      itemActions: (record: RecordType) => {
        if (!isEquipmentRecord(record)) return []

        const canDelete = shouldShowDeleteButton(
          normalizeNodeType(nodeId),
          record.definedAtNodeType
        )

        const isInherited = record.readonly
        const canEdit = !isInherited || record.isOverride
        const canOverride = isInherited && !record.isOverride

        const actions = []

        if (canManageTree && canEdit) {
          actions.push({
            label: i18n.t('job_catalog.equipment.actions.edit'),
            icon: Pencil,
            onClick: () => handleEditEquipment(record),
          })
        }

        if (canManageTree && canOverride) {
          actions.push({
            label: i18n.t('job_catalog.equipment.actions.override'),
            description: i18n.t('job_catalog.equipment.actions.override_description', {
              node: normalizeNodeType(nodeId),
            }),
            icon: Pencil,
            onClick: () => handleOverrideEquipment(record),
          })
        }

        if (canManageTree && canDelete) {
          if (record.isOverride) {
            actions.push({
              label: i18n.t('job_catalog.equipment.actions.remove_override'),
              description: i18n.t('job_catalog.equipment.actions.remove_override_description', {
                node: normalizeNodeType(record.definedAtNodeType),
              }),
              icon: Reset,
              onClick: () => handleDeleteEquipment(record),
            })
          } else {
            actions.push({
              label: i18n.t('common.delete'),
              icon: Delete,
              onClick: () => handleDeleteEquipment(record),
              critical: true,
            })
          }
        }

        return actions
      },
      mapVariables: () => ({
        nodeUuid: nodeId,
      }),
    },
    [nodeId, refreshTrigger]
  )

  const submitEquipment = async (equipment: EquipmentData[]) => {
    if (equipment.length === 0) {
      setShowEquipmentModal(false)
      return
    }

    const equipmentItem = equipment[0]

    if (!equipmentItem) {
      setShowEquipmentModal(false)
      return
    }

    const current = currentEquipment.find((c) => c.id === equipmentItem.id)
    if (current && current.readonly) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('job_catalog.equipment.errors.readonly_equipment'),
      })
      setShowEquipmentModal(false)
      return
    }

    const valueJson = JSON.stringify({
      attribute_id: equipmentItem.id,
      it_asset_model_id: equipmentItem.id,
      brand: equipmentItem.brand,
      name: equipmentItem.name,
      model_name: equipmentItem.name,
      type_name: equipmentItem.typeName,
      typeName: equipmentItem.typeName,
    })

    const result = await addAttribute(
      'it_management',
      company.id,
      nodeId,
      valueJson,
      equipmentItem.id
    )

    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('job_catalog.equipment.success.assigned'),
        time: 5000,
      })
      setRefreshTrigger((prev) => prev + 1)
    }

    track(trackingEvents.jobCatalog.assignDeviceSubmit, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(nodeId),
      node_id: nodeId,
      device_name: equipmentItem.name,
      device_brand: equipmentItem.brand,
      device_category: equipmentItem.typeName,
      is_override: false,
    })

    setShowEquipmentModal(false)
  }

  const handleEditEquipment = (record: EquipmentRecord) => {
    track(trackingEvents.jobCatalog.editDeviceStart, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(nodeId),
      node_id: nodeId,
      device_name: record.name,
      device_brand: record.brand,
      device_category: record.typeName,
      from: 'edit_action',
    })
    setEditingEquipment(record)
    setIsOverrideMode(false)
    setShowEditModal(true)
  }

  const handleOverrideEquipment = (record: EquipmentRecord) => {
    track(trackingEvents.jobCatalog.editDeviceStart, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(nodeId),
      node_id: nodeId,
      device_name: record.name,
      device_brand: record.brand,
      device_category: record.typeName,
      from: 'override_action',
    })
    setEditingEquipment(record)
    setIsOverrideMode(true)
    setShowEditModal(true)
  }

  const handleUpdateEquipment = async (equipment: {
    id: string
    brand: string
    name: string
    typeName: string
  }) => {
    if (!editingEquipment) return

    if (isOverrideMode) {
      // Create a new override instead of updating the existing record
      const valueJson = JSON.stringify({
        attribute_id: equipment.id,
        it_asset_model_id: equipment.id,
        brand: equipment.brand,
        name: equipment.name,
        model_name: equipment.name,
        type_name: equipment.typeName,
        typeName: equipment.typeName,
        isOverride: true,
        originalNodeAttributeId: editingEquipment.nodeAttributeId,
        originalTypeName: editingEquipment.typeName,
        originalInheritedFrom: editingEquipment.inheritedFrom,
      })

      const result = await addAttribute(
        'it_management',
        company.id,
        nodeId,
        valueJson,
        equipment.id,
        'override'
      )

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: errors.errors || i18n.t('errors.generic_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('job_catalog.equipment.success.overridden'),
          time: 5000,
        })
        setRefreshTrigger((prev) => prev + 1)
      }

      track(trackingEvents.jobCatalog.editDeviceSubmit, {
        event_category: 'Organizations',
        node_type: normalizeNodeType(nodeId),
        node_id: nodeId,
        device_name: equipment.name,
        device_brand: equipment.brand,
        device_category: equipment.typeName,
        is_override: true,
      })
    } else {
      // Regular update
      const valueJson = JSON.stringify({
        attribute_id: equipment.id,
        it_asset_model_id: equipment.id,
        brand: equipment.brand,
        name: equipment.name,
        model_name: equipment.name,
        type_name: equipment.typeName,
        typeName: equipment.typeName,
      })

      const result = await updateEquipment(
        editingEquipment.nodeAttributeId,
        company.id,
        nodeId,
        valueJson
      )

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: errors.errors || i18n.t('errors.generic_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('job_catalog.equipment.success.updated'),
          time: 5000,
        })
        setRefreshTrigger((prev) => prev + 1)
      }

      track(trackingEvents.jobCatalog.editDeviceSubmit, {
        event_category: 'Organizations',
        node_type: normalizeNodeType(nodeId),
        node_id: nodeId,
        device_name: equipment.name,
        device_brand: equipment.brand,
        device_category: equipment.typeName,
        is_override: false,
      })
    }

    setShowEditModal(false)
    setEditingEquipment(null)
    setIsOverrideMode(false)
  }

  const formatSublevelCounts = (): ReactNode => {
    const counts = []
    const normalizedType = normalizeNodeType(_nodeType)

    if (normalizedType === NODE_TYPES.ROOT || normalizedType === NODE_TYPES.COMPANY) {
      if (sublevelCounts.families > 0) {
        counts.push(
          `${sublevelCounts.families} ${
            sublevelCounts.families === 1
              ? i18n.t('job_catalog.equipment.node_types.family')
              : i18n.t('job_catalog.equipment.node_types.families')
          }`
        )
      }
      if (sublevelCounts.functions > 0) {
        counts.push(
          `${sublevelCounts.functions} ${
            sublevelCounts.functions === 1
              ? i18n.t('job_catalog.equipment.node_types.function')
              : i18n.t('job_catalog.equipment.node_types.functions')
          }`
        )
      }
      if (sublevelCounts.roles > 0) {
        counts.push(
          `${sublevelCounts.roles} ${
            sublevelCounts.roles === 1
              ? i18n.t('job_catalog.equipment.node_types.role')
              : i18n.t('job_catalog.equipment.node_types.roles')
          }`
        )
      }
      if (sublevelCounts.levels > 0) {
        counts.push(
          `${sublevelCounts.levels} ${
            sublevelCounts.levels === 1
              ? i18n.t('job_catalog.equipment.node_types.level')
              : i18n.t('job_catalog.equipment.node_types.levels')
          }`
        )
      }
    } else if (normalizedType === NODE_TYPES.FAMILY) {
      if (sublevelCounts.functions > 0) {
        counts.push(
          `${sublevelCounts.functions} ${
            sublevelCounts.functions === 1
              ? i18n.t('job_catalog.equipment.node_types.function')
              : i18n.t('job_catalog.equipment.node_types.functions')
          }`
        )
      }
      if (sublevelCounts.roles > 0) {
        counts.push(
          `${sublevelCounts.roles} ${
            sublevelCounts.roles === 1
              ? i18n.t('job_catalog.equipment.node_types.role')
              : i18n.t('job_catalog.equipment.node_types.roles')
          }`
        )
      }
      if (sublevelCounts.levels > 0) {
        counts.push(
          `${sublevelCounts.levels} ${
            sublevelCounts.levels === 1
              ? i18n.t('job_catalog.equipment.node_types.level')
              : i18n.t('job_catalog.equipment.node_types.levels')
          }`
        )
      }
    } else if (normalizedType === NODE_TYPES.FUNCTION) {
      if (sublevelCounts.roles > 0) {
        counts.push(
          `${sublevelCounts.roles} ${
            sublevelCounts.roles === 1
              ? i18n.t('job_catalog.equipment.node_types.role')
              : i18n.t('job_catalog.equipment.node_types.roles')
          }`
        )
      }
      if (sublevelCounts.levels > 0) {
        counts.push(
          `${sublevelCounts.levels} ${
            sublevelCounts.levels === 1
              ? i18n.t('job_catalog.equipment.node_types.level')
              : i18n.t('job_catalog.equipment.node_types.levels')
          }`
        )
      }
    } else if (normalizedType === NODE_TYPES.ROLE) {
      if (sublevelCounts.levels > 0) {
        counts.push(
          `${sublevelCounts.levels} ${
            sublevelCounts.levels === 1
              ? i18n.t('job_catalog.equipment.node_types.level')
              : i18n.t('job_catalog.equipment.node_types.levels')
          }`
        )
      }
    }

    if (counts.length === 0) {
      return ''
    }

    return (
      <>
        <Box paddingLeft="s16" paddingTop="s8">
          {counts.map((count, index) => (
            <Box key={index} paddingBottom="s4">
              <Text size="400">• {count}</Text>
            </Box>
          ))}
        </Box>
      </>
    )
  }

  const handleDeleteEquipment = (record: EquipmentRecord) => {
    const sublevelText = formatSublevelCounts()

    track(trackingEvents.jobCatalog.removeDeviceStart, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(nodeId),
      node_id: nodeId,
      device_name: record.name,
      device_brand: record.brand,
      device_category: record.typeName,
      is_override: record.isOverride,
    })

    openDialog({
      illustration: 'delete',
      title: i18n.t('job_catalog.equipment.remove_dialog.title', {
        brand: record.brand,
        name: record.name,
      }),
      description: (
        <>
          {sublevelText ? (
            <>
              {i18n.t('job_catalog.equipment.remove_dialog.description_with_affected')}{' '}
              {sublevelText} {i18n.t('job_catalog.equipment.remove_dialog.description_simple')}
            </>
          ) : (
            i18n.t('job_catalog.equipment.remove_dialog.description_simple')
          )}
        </>
      ),
      onConfirm: async () => {
        const result = await deleteAttribute(record.nodeAttributeId, nodeId)

        const handled = handleResult(result, (errors) => {
          app.setFlash({
            type: 'wrong',
            message: errors.errors || i18n.t('errors.generic_error'),
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('job_catalog.equipment.success.removed'),
            time: 5000,
          })
          setRefreshTrigger((prev) => prev + 1)
        }

        track(trackingEvents.jobCatalog.removeDeviceSubmit, {
          event_category: 'Organizations',
          node_type: normalizeNodeType(nodeId),
          node_id: nodeId,
          device_name: record.name,
          device_brand: record.brand,
          device_category: record.typeName,
          is_override: record.isOverride,
        })
      },
    })
  }

  if (shouldShowUpsell) {
    return (
      <UpsellingKit.EmptyState
        moduleRequested={AvailableModules.it_management}
        featureRequested={Features.it_management_v2_core}
        requestReason={ValidReasons.MissingAddon}
        requestSource="job_catalog_equipment_tab"
        title={i18n.t('job_catalog.nodes.empty_state.it_management.upsell.title')}
        description={i18n.t('job_catalog.nodes.empty_state.it_management.upsell.description')}
        buttonLabel={i18n.t('job_catalog.nodes.empty_state.it_management.upsell.button_label')}
      />
    )
  }

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            emoji: '💻',
            title: i18n.t('job_catalog.nodes.empty_state.it_management.title'),
            description: i18n.t('job_catalog.nodes.empty_state.it_management.description'),
            actions: canManageTree
              ? [
                  {
                    label: i18n.t('job_catalog.nodes.empty_state.it_management.actions.assign'),
                    onClick: () => {
                      track(trackingEvents.jobCatalog.assignDeviceStart, {
                        event_category: 'Organizations',
                        node_type: normalizeNodeType(nodeId),
                        node_id: nodeId,
                        from: 'empty_state',
                      })
                      setShowEquipmentModal(true)
                    },
                    icon: Plus,
                  },
                ]
              : [],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('job_catalog.equipment.table.model'),
                  render: (record: RecordType) => {
                    if (!isEquipmentRecord(record)) return { type: 'text', value: '' }
                    return {
                      type: 'text',
                      value: `${record.brand} ${record.name}`,
                    }
                  },
                },
                {
                  label: i18n.t('job_catalog.equipment.table.category'),
                  render: (record: RecordType) => {
                    if (!isEquipmentRecord(record)) return { type: 'text', value: '' }
                    return {
                      type: 'tag',
                      value: {
                        label: safeAssetTranslateType(record.typeName),
                        icon: safeAssetTypeAsIcon(record.typeName),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('job_catalog.equipment.table.inherited_from'),
                  render: (record: RecordType) => {
                    if (!isEquipmentRecord(record)) return { type: 'tag', value: { label: '' } }

                    if (!record.inheritedFrom) {
                      return '-'
                    }

                    if (record.isOverride) {
                      return {
                        type: 'tag',
                        value: {
                          label: i18n.t('job_catalog.equipment.status.override_from', {
                            source: record.inheritedFrom,
                          }),
                          color: 'blue',
                          icon: record.icon,
                        },
                      }
                    }

                    return {
                      type: 'tag',
                      value: { label: record.definedAt, icon: record.icon },
                    }
                  },
                },
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
      {showEquipmentModal && (
        <ItAssetModelFinder
          currentEquipment={currentEquipment}
          onSubmit={submitEquipment}
          onClose={() => setShowEquipmentModal(false)}
        />
      )}
      {showEditModal && editingEquipment && (
        <EditEquipmentModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setEditingEquipment(null)
            setIsOverrideMode(false)
          }}
          onSubmit={handleUpdateEquipment}
          currentEquipment={{
            id: editingEquipment.id,
            brand: editingEquipment.brand,
            name: editingEquipment.name,
            typeName: editingEquipment.typeName,
          }}
          isOverrideMode={isOverrideMode}
          inheritedFrom={editingEquipment.inheritedFrom}
        />
      )}
    </>
  )
}
